/**
 * Paint expressions for the three MERCI probe layers, carried over from
 * TITAN_dev (components/MapLayers/Probe/*) so the same data reads the same way
 * in both apps. The colour scales are TITAN's, not re-picked here: a segment
 * that looks severe on MERCI must look severe here.
 */

export const API = 'https://api.missouri-titan.com';
export const PROBE_TILES = `${API}/v1/probe/tiles/{z}/{x}/{y}.pbf`;

/** Jam severity, free-flow green to severe red. */
const JAM_RAMP = ['#2ECC71', '#F1C40F', '#E67E22', '#E74C3C'];

/** Speed change: a diverging pair, warm for slower and cool for faster. */
export const SLOWER = '#e66767';
export const FASTER = '#3987e5';
/** A change a work-zone history explains, rather than a new one. */
export const CONSTRUCTION = '#fab219';

export const MIN_SEC_PER_MI = 30;
export const MIN_PTI = 2.0;

/** Planning-time-index ramp, least to most unreliable. */
export const PTI_RAMP: [number, string][] = [
    [2.0, '#3b6ea5'],
    [2.5, '#35a7c2'],
    [3.25, '#4fd8d0'],
    [4.5, '#bff7f0'],
];

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const probePaint: any = {
    'line-color': [
        'case',
        // No reading at all, and jam factor 10, which HERE uses for a closure.
        ['!', ['has', 'ss_jam_factor']], '#5a6a78',
        ['>=', ['get', 'ss_jam_factor'], 10], '#9e9e9e',
        ['interpolate', ['linear'], ['get', 'ss_jam_factor'],
            0, JAM_RAMP[0], 4, JAM_RAMP[1], 7, JAM_RAMP[2], 9.9, JAM_RAMP[3]],
    ],
    'line-width': ['interpolate', ['linear'], ['zoom'], 6, 1, 11, 2.5, 16, 5],
    'line-opacity': 0.9,
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const anomalyPaint: any = {
    'line-color': ['case', ['get', 'slower'], SLOWER, FASTER],
    // Width carries the size of the change, so the worst are findable at
    // statewide zoom without hunting.
    'line-width': ['interpolate', ['linear'], ['zoom'],
        6, ['interpolate', ['linear'], ['abs', ['get', 'sec_per_mi']], 30, 1.5, 120, 5],
        16, ['interpolate', ['linear'], ['abs', ['get', 'sec_per_mi']], 30, 5, 120, 14]],
    'line-opacity': 0.9,
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const reliabilityPaint: any = {
    'line-color': ['interpolate', ['linear'], ['get', 'pti'],
        ...PTI_RAMP.flatMap(([v, c]) => [v, c])],
    'line-width': ['interpolate', ['linear'], ['zoom'],
        6, ['interpolate', ['linear'], ['get', 'pti'], 2.0, 1.4, 4.5, 4],
        11, ['interpolate', ['linear'], ['get', 'pti'], 2.0, 2.5, 4.5, 7],
        16, ['interpolate', ['linear'], ['get', 'pti'], 2.0, 4, 4.5, 12]],
    'line-opacity': 0.9,
};

/* --- work zones ---------------------------------------------------------
   MERCI draws a work zone as a thick amber line with black dashes struck
   over it, which reads as barrier tape at any zoom. Both passes are kept:
   the amber alone is easy to mistake for a congestion colour. */

export const WORKZONE = '#FFA726';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const workzonePaint: any = {
    'line-color': WORKZONE,
    'line-width': 8,
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const workzoneStripePaint: any = {
    'line-color': '#000000',
    'line-width': 4,
    'line-dasharray': [1.5, 2],
    'line-opacity': 0.7,
};
